<template>
<div class="bytee-quiz" :class="questionFadeClass">
  <error-renderer :message="errorMessage" :severity="errorSeverity"
                  @close="errorMessage = ''"></error-renderer>

  <navigation
    :numQuestion="numQuestion"
    :questions="questions"
    :quiz="quiz"
    :resolveResolution="resolveResolution"
    :timerText="timerText"
  >
  </navigation>

  <start
      v-show="numQuestion === -1"
      :quiz="quiz"
      :numStartPage="numStartPage"
      @start="startQuiz"
      @nextStartPage="nextStartPage"
  >
  </start>

  <question
      v-if="numQuestion !== -1 && numQuestion !== questions.length"
      :question.sync="activeQuestion"
      :resolve="resolveResolution"
      @answer="answer"
  >
  </question>

  <final
      v-if="numQuestion === questions.length && !resolveResolution"
      :questions="questions"
      @resolveQuiz="resolveQuiz"
      @goToQuestion="viewQuestion"
      @previousQuestion="previousQuestion"
  >
  </final>

  <result-irc
      v-if="resolveResolution && numQuestion >= questions.length"
      :questions="questions"
      :scoring="scoring"
      :winnerIndex="winnerIndex"
      :testModeResolutionWasUsed="testModeResolutionWasUsed"
      @viewQuestion="viewQuestion"
  >
  </result-irc>

  <!-- Quiz Nav, TODO MOVE -->
  <div class="quiz-nav" v-if="numQuestion !== -1 && numQuestion !== questions.length
  && quiz.enableHistoryNavigation">
    <div class="columns">
      <div class="column col-8 col-md-12">
        <button
            class="btn btn-primary"
            v-if="!resolveResolution && numQuestion !== 0"
            @click="firstQuestion"
        >
          {{ $t('back_to_start') }}
        </button>
        <button
            class="btn btn-primary"
            v-if="!resolveResolution && numQuestion !== 0"
            @click="previousQuestion"
        >
          {{ $t('previous_question') }}
        </button>
        <button
            class="btn btn-primary"
            v-if="enableQuestionFlags && !resolveResolution && numQuestion >= 0"
            @click="markQuestion"
        >
          <span v-show="activeQuestion.marked">{{ $t('clear_flag') }}</span>
          <span v-show="!activeQuestion.marked">{{ $t('flag_question') }}</span>
        </button>
        <button
            class="btn btn-primary"
            v-if="resolveResolution"
            @click="numQuestion = questions.length"
        >
          {{ $t('back_to_results') }}
        </button>
      </div>
      <div class="column col-4 col-md-auto text-right" v-if="quiz.apiEndpoint">
        <router-link to="/suggest" target="_blank">{{ $t('suggest_question') }}</router-link>
        |
        <router-link :to="{name: 'report', params: { id: activeQuestion._id } }"
                     target="_blank">
          {{ $t('report_question') }}
        </router-link>
      </div>
    </div>
  </div>

  <TestMode :questions="questions"
            :scoring="scoring"
            :winnerIndex="winnerIndex"
            :resolveResolution="resolveResolution"
            :testModeResolutionActive="testModeResolutionActive"
            :testModeResolutionWasUsed="testModeResolutionWasUsed"
            :quiz="quiz" />
  <div v-if="testModeResolutionActive" class="page-overlay"></div>
</div>
</template>

<script>
import isEqual from 'lodash/isEqual';
import shuffle from 'lodash/shuffle';

import Start from './Start';
import Question from './Question';
// import Result from './Result';
import ResultIrc from '@/views/ResultIrc';
import ErrorRenderer from './parts/ErrorRenderer';
import Final from './Final';
import TestMode from '@/views/parts/TestMode';
import Navigation from '@/views/Navigation';

// @DEV: DEV TEST MODE (does custom magic to skip some elements during development)
const DEV_TEST_MODE = false;

export default {
  name: 'home',
  components: {
    Navigation,
    TestMode,
    Final,
    ErrorRenderer,
    // Result,
    ResultIrc,
    Question,
    Start,
  },
  data() {
    return {
      // For now
      quiz: window.Quiz,
      questions: [],
      scoring: [],

      // Simple Quiz State
      timer: null,
      timerText: '',

      numStartPage: 1,

      numQuestion: -1,
      activeQuestion: {},

      enableQuestionFlags: false,
      enableHistoryNavigation: false,

      // Results
      resolveResolution: false,
      winnerIndex: 1,

      // Error Handling
      errorMessage: '',
      errorSeverity: 'warning',

      testResolveTimer: null,

      testDevMode: false,
      testModeResolutionActive: false,
      testModeResolutionWasUsed: false,

      questionFadeClass: '',
    };
  },
  created() {
    if (window.Quiz.customizeQuiz) {
      this.$router.push('/customize');
      return;
    }

    // Very simple load - questions
    this.$http
      .get(this.quiz.questionsEndpoint)
      .then((response) => {
        this.prepareQuiz(response.data);
      })
      .catch((error) => {
        this.errorMessage = error;
        this.errorSeverity = 'error';
      });

    // Very simple load - scoring
    this.$http
      .get(this.quiz.scoringEndpoint)
      .then((response) => {
        this.scoring = response.data;
      })
      .catch((error) => {
        this.errorMessage = error;
        this.errorSeverity = 'error';
      });

    // Keydown listener (allows to choose answers using 1..0 keys instead of clicking)
    document.addEventListener('keydown', (event) => {
      const answer = document.querySelector(`[data-question-number="${event.key}"]`);
      if (answer) {
        answer.click();
      }
    });
  },
  methods: {
    startQuiz() {
      if (this.quiz.duration > 0) {
        this.startTimer(this.quiz.duration * 60);
      }
      this.nextQuestion();
    },

    nextStartPage() {
      this.numStartPage += 1;
    },

    /**
     * Prepare the quiz question
     * @param questions {Array} Questions for this Quiz
     */
    prepareQuiz(questions) {
      if (this.quiz.randomize) {
        // First shuffle the question ordering
        questions = shuffle(questions);
      }

      // Shrink questions
      if (this.quiz.questionCount && questions.length > this.quiz.questionCount) {
        questions = questions.slice(0, this.quiz.questionCount);
      }

      // Question answers
      // Shuffle questions, stupid hack TODO Improve
      questions.forEach((question, index) => {
        question.index = index;

        if (!this.quiz.randomize) {
          return;
        }

        if (question.kind === 'text') {
          return;
        }

        question.answers.forEach((answer, index) => {
          if (question.resolution.includes(index)) {
            answer.isCorrect = true;
          }
        });

        question.answers = shuffle(question.answers);
        question.resolution = [];

        question.answers.forEach((answer, index) => {
          if (answer.isCorrect) {
            question.resolution.push(index);
            delete answer.isCorrect;
          }
        });
      });

      this.questions = questions;
    },

    startTimer(duration) {
      this.timer = setInterval(() => {
        let minutes = parseInt(duration / 60, 10);
        let seconds = parseInt(duration % 60, 10);

        minutes = minutes < 10 ? `0${minutes}` : minutes;
        seconds = seconds < 10 ? `0${seconds}` : seconds;

        this.timerText = `${minutes}:${seconds}`;

        duration -= 1;

        if (duration < 0) {
          duration = 0;

          // Finish Quiz
          this.numQuestion = this.questions.length;
          this.resolveResolution = true;
          clearInterval(this.timer);
        }
      }, 1000);
    },

    answer(val) {
      // Checking done here for convenience
      this.activeQuestion.answer = val;

      if (this.activeQuestion.kind !== 'text') {
        this.activeQuestion.isCorrect = isEqual(val, this.activeQuestion.resolution);
      } else {
        // Multiple answers could be correct for text questions
        this.activeQuestion.isCorrect = this.activeQuestion.resolution.includes(val);
      }

      this.nextQuestion();
    },

    // Navigation
    firstQuestion() {
      this.numQuestion = 0;

      this.activeQuestion = this.questions[this.numQuestion];
    },

    previousQuestion() {
      this.numQuestion -= 1;

      this.activeQuestion = this.questions[this.numQuestion];
    },

    async initQuestionFade() {
      this.questionFadeClass = 'fade';
      this.$nextTick();
      setTimeout(() => {
        this.questionFadeClass = '';
      }, 100);
    },

    nextQuestion() {
      this.initQuestionFade();
      this.numQuestion += 1;

      if (this.numQuestion === this.questions.length) {
        return;
      }

      this.activeQuestion = this.questions[this.numQuestion];
    },

    markQuestion() {
      this.activeQuestion.marked = !this.activeQuestion.marked;

      this.nextQuestion();
    },

    sendStats() {
      if (!window.Quiz.statisticEndpoint) {
        return;
      }

      // Report users result
      this.$http
        .post(`${window.Quiz.apiEndpoint}/stats`, this.questions)
        .then(() => {
        })
        .catch((error) => {
          this.errorMessage = error;
          this.errorSeverity = 'warning';
        });
    },

    // Result
    viewQuestion(index) {
      this.numQuestion = index;
      this.activeQuestion = this.questions[this.numQuestion];
    },

    resolveQuiz() {
      this.sendStats();
      this.resolveResolution = true;
      clearInterval(this.timer);
    },
  },
};
</script>

<style>
.page-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 800;
  backdrop-filter: blur(8px);
}
.page-overlay::before {
  content: "";
  display: block;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: #FFFFFF;
  opacity: .7;
}
.bytee-quiz.fade .quiz-question {
  animation: fade-in .2s ease-in-out 0s 1 normal forwards;
}
</style>
